import React from 'react';

import { connect } from '../../../base/redux';
import { closeImagePreview } from '../../actions.any';

const IMAGE_STYLES = {
    maxWidth: '100%',
    maxHeight: '90vh',
    objectFit: 'contain',
    borderRadius: '8px'
};

const AUDIO_CONTAINER_STYLES = {
    background: 'white',
    padding: '40px',
    borderRadius: '8px',
    textAlign: 'center'
};

const AUDIO_TITLE_STYLES = {
    marginBottom: '20px'
};

const AUDIO_ICON_STYLES = {
    fontSize: '48px'
};

const AUDIO_TEXT_STYLES = {
    marginTop: '10px',
    fontSize: '16px'
};

const AUDIO_PLAYER_STYLES = {
    width: '100%',
    maxWidth: '500px'
};

const PDF_IFRAME_STYLES = {
    width: '90vw',
    height: '90vh',
    border: 'none',
    borderRadius: '8px',
    background: 'white'
};

const TEXT_CONTAINER_STYLES = {
    background: 'white',
    padding: '20px',
    borderRadius: '8px',
    maxWidth: '90vw',
    maxHeight: '90vh',
    overflow: 'auto'
};

const TEXT_IFRAME_STYLES = {
    width: '100%',
    minHeight: '70vh',
    border: 'none'
};

const UNSUPPORTED_CONTAINER_STYLES = {
    background: 'white',
    padding: '40px',
    borderRadius: '8px',
    textAlign: 'center'
};

const UNSUPPORTED_ICON_STYLES = {
    fontSize: '48px'
};

const UNSUPPORTED_TITLE_STYLES = {
    marginTop: '20px',
    fontSize: '18px'
};

const UNSUPPORTED_SUBTITLE_STYLES = {
    marginTop: '10px',
    fontSize: '14px'
};

const DOWNLOAD_LINK_STYLES = {
    display: 'inline-block',
    marginTop: '20px',
    padding: '10px 20px',
    background: '#4b6790',
    textDecoration: 'none',
    borderRadius: '4px',
    color: 'white'
};

const MODAL_OVERLAY_STYLES = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    cursor: 'pointer',
    backgroundColor: '#00000080'
};

const MODAL_CONTENT_STYLES = {
    position: 'relative',
    maxWidth: '95%',
    maxHeight: '95%'
};

const CLOSE_BUTTON_STYLES = {
    position: 'absolute',
    top: '0px',
    right: '0px',
    background: 'transparent',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '10px',
    lineHeight: '1',
    zIndex: 10000
};

/**
 * Renders the file preview modal for images, videos, audio, PDFs, and other formats.
 *
 * @param {Object} props - Component props.
 * @returns {React$Element<*>|null}
 */
function ImagePreviewModal({ showImagePreview, previewImageUrl, fileName, dispatch }) {
    if (!showImagePreview || !previewImageUrl) {
        return null;
    }

    const handleClose = () => {
        dispatch(closeImagePreview());
    };

    /**
     * Gets the file extension from filename or URL.
     *
     * @returns {string} - File extension in lowercase
     */
    const getFileExtension = () => {
        const name = fileName || previewImageUrl;
        const extension = name
            .split('.')
            .pop()
            ?.toLowerCase();

        return extension || '';
    };

    /**
     * Determines the file type category.
     *
     * @returns {string} - File type category
     */
    const getFileType = () => {
        const ext = getFileExtension();

        // Image formats
        if ([ 'jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico' ].includes(ext)) {
            return 'image';
        }

        // Video formats
        if ([ 'mp4', 'webm', 'ogg', 'avi', 'mkv', 'flv', 'wmv' ].includes(ext)) {
            return 'video';
        }

        // Audio formats
        if ([ 'mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma' ].includes(ext)) {
            return 'audio';
        }

        // PDF
        if (ext === 'pdf') {
            return 'pdf';
        }

        // Text/Code formats
        if ([ 'txt', 'md', 'json', 'xml', 'csv', 'log', 'rtf', 'odt' ].includes(ext)) {
            return 'text';
        }

        return 'unsupported';
    };

    /**
     * Renders the appropriate preview based on file type.
     *
     * @returns {React$Element<*>}
     */
    const renderPreview = () => {
        const fileType = getFileType();
        const waxW = { maxWidth: '100%' };

        switch (fileType) {
        case 'image':
            return (
                <img
                    alt = { fileName || 'Preview' }
                    onClick = { e => e.stopPropagation() }
                    src = { previewImageUrl }
                    style = { IMAGE_STYLES } />
            );

        case 'video':
            return (
                <div
                    className = 'intro-y col-span-12 lg:col-span-12'
                    onClick = { e => e.stopPropagation() }>
                    <video
                        controls = { true }
                        height = '500'
                        style = { waxW }
                        width = '100%'>
                        <source
                            src = { previewImageUrl }
                            type = { `video/${getFileExtension() || 'mp4'}` } />
                        Your browser does not support the video tag.
                    </video>
                </div>
            );

        case 'audio':
            return (
                <div
                    onClick = { e => e.stopPropagation() }
                    style = { AUDIO_CONTAINER_STYLES }>
                    <div style = { AUDIO_TITLE_STYLES }>
                        <span style = { AUDIO_ICON_STYLES }>🎵</span>
                        <p style = { AUDIO_TEXT_STYLES }>
                            {fileName || 'Audio File'}
                        </p>
                    </div>
                    <audio
                        controls = { true }
                        style = { AUDIO_PLAYER_STYLES }>
                        <source
                            src = { previewImageUrl }
                            type = { `audio/${getFileExtension()}` } />
                        Your browser does not support the audio tag.
                    </audio>
                </div>
            );

        case 'pdf':
            return (
                <iframe
                    onClick = { e => e.stopPropagation() }
                    src = { previewImageUrl }
                    style = { PDF_IFRAME_STYLES }
                    title = { fileName || 'PDF Preview' } />
            );

        case 'text':
            return (
                <div
                    onClick = { e => e.stopPropagation() }
                    style = { TEXT_CONTAINER_STYLES }>
                    <iframe
                        src = { previewImageUrl }
                        style = { TEXT_IFRAME_STYLES }
                        title = { fileName || 'Text Preview' } />
                </div>
            );

        default:
            return (
                <div
                    onClick = { e => e.stopPropagation() }
                    style = { UNSUPPORTED_CONTAINER_STYLES }>
                    <span style = { UNSUPPORTED_ICON_STYLES }>📄</span>
                    <p style = { UNSUPPORTED_TITLE_STYLES }>
                        Preview not available for this file type
                    </p>
                    <p style = { UNSUPPORTED_SUBTITLE_STYLES }>
                        {fileName || 'Unknown file'}
                    </p>
                    <a
                        download = { true }
                        href = { previewImageUrl }
                        onClick = { e => e.stopPropagation() }
                        style = { DOWNLOAD_LINK_STYLES }>
                        Download File
                    </a>
                </div>
            );
        }
    };

    return (
        <div
            className = 'image-preview-modal'
            onClick = { handleClose }
            style = { MODAL_OVERLAY_STYLES }>
            <div style = { MODAL_CONTENT_STYLES }>
                <button
                    onClick = { handleClose }
                    style = { CLOSE_BUTTON_STYLES }>
                    ✕
                </button>
                {renderPreview()}
            </div>
        </div>
    );
}

/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code ImagePreviewModal} component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {Object}
 */
function mapStateToProps(state) {
    const { imagePreview } = state['features/chat'];

    return {
        showImagePreview: imagePreview.isOpen,
        previewImageUrl: imagePreview.imageUrl,
        fileName: imagePreview.fileName
    };
}

export default connect(mapStateToProps)(ImagePreviewModal);
